import { config } from './config'
import type {
  DataResponse,
  HallEntry,
  LeaderboardResponse,
  MatchEntry,
  PageResponse,
  PlayerProfile,
  PlayerRef,
  ServerStatus,
  TournamentBracket,
  TournamentDetail,
} from './types'

interface ErrorResponse {
  error?: { code?: string; message?: string }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    headers: { Accept: 'application/json' },
    signal,
  })
  const body = (await response.json()) as T | ErrorResponse
  if (!response.ok) {
    const error = (body as ErrorResponse).error
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN_ERROR',
      error?.message ?? 'Dati temporaneamente non disponibili.',
    )
  }
  return body as T
}

function query(parameters: Record<string, string | number | undefined>): string {
  const values = new URLSearchParams()
  Object.entries(parameters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') values.set(key, String(value))
  })
  const serialized = values.toString()
  return serialized ? `?${serialized}` : ''
}

export const api = {
  serverStatus: (signal?: AbortSignal) =>
    request<DataResponse<ServerStatus>>('/server/status', signal),

  leaderboard: (page: number, limit = 25, signal?: AbortSignal) =>
    request<LeaderboardResponse>(
      `/leaderboard${query({ page, limit })}`,
      signal,
    ),

  searchPlayers: (value: string, signal?: AbortSignal) =>
    request<PageResponse<PlayerRef>>(
      `/players/search${query({ q: value, limit: 10 })}`,
      signal,
    ),

  player: (uuid: string, signal?: AbortSignal) =>
    request<DataResponse<PlayerProfile>>(`/players/${uuid}`, signal),

  matches: (uuid: string, page: number, signal?: AbortSignal) =>
    request<PageResponse<MatchEntry>>(
      `/players/${uuid}/matches${query({ page, limit: 10 })}`,
      signal,
    ),

  activeTournament: (signal?: AbortSignal) =>
    request<DataResponse<TournamentDetail>>('/tournaments/active', signal),

  tournament: (id: string, signal?: AbortSignal) =>
    request<DataResponse<TournamentDetail>>(`/tournaments/${id}`, signal),

  bracket: (id: string, signal?: AbortSignal) =>
    request<DataResponse<TournamentBracket>>(
      `/tournaments/${id}/bracket`,
      signal,
    ),

  hallOfFame: (signal?: AbortSignal) =>
    request<PageResponse<HallEntry>>(
      '/tournaments/hall-of-fame?limit=18',
      signal,
    ),
}
